import { useAuthenticator } from "@aws-amplify/ui-react";
import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import { Tooltip } from "@mui/material";
import ModalBox from "components/Modal";
import CopyToClipboard from "components/shared/CopyToClipboard";
import DeleteModal from "components/shared/DeleteModal";
import LastElementTable from "components/shared/LastElementTable";
import type { LastElementTableColumns } from "components/shared/LastElementTable/type";
import ProgressBar from "components/shared/ProgressBar";
import StyledButton from "components/shared/StyledButton";
import { StyledMenuItem, StyledSelect } from "components/shared/StyledSelect";
import StyledTextField from "components/shared/StyledTextField/StyledTextField";
import type { ThreeDotMenuOptions } from "components/shared/ThreeDotMenu";
import ThreeDotMenu from "components/shared/ThreeDotMenu";
import { ROLES } from "containers/ScopeContext/roles.enum";
import { SmallBody } from "helpers/TypographyComponents";
import {
    extractCustomRoleId,
    formatCustomRoleId,
    formatLocaleDateString,
    getCognitoUserAttribute,
    isCustomRole,
    isObjectEmpty,
} from "helpers/utils";
import { useCompanySettings } from "hooks/useCompanySettings";
import { useNormalizedCompanyUsers } from "hooks/useNormalizedCompanyUsers";
import type { PossibleUserRole } from "hooks/usePossibleUserRoles";
import { usePossibleUserRoles } from "hooks/usePossibleUserRoles";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteUser, getUserListByCompany, sendInvitation } from "store/adminPanelSlice";
import { fetchCompanyInfo } from "store/settingsStore";
import { useAppDispatch, useAppSelector } from "store/store";
import type { TAccountType } from "store/types";
import type { IUser } from "types";

import variables from "../../assets/styles/_variables.module.scss";
import AskForSeatsModal from "./AskForSeatsModal";
import styles from "./style.module.scss";
import type { IDefaultUserValues } from "./UserFormModal";
import UserFormModal from "./UserFormModal";
import { getUserRolesLabel } from "./userRoles";

type UserToDelete = { firstname: string; lastname: string; uuid: string };
type TUserFormModal = {
    open: boolean;
    userId: number | null;
    defaultValues: IDefaultUserValues;
};

const MemberList = () => {
    const [t] = useTranslation();
    const dispatch = useAppDispatch();
    const { getCompanySettings } = useCompanySettings();
    const [isAddMemberModalOpen, setIsModalOpen] = useState(false);
    const { user } = useAuthenticator((context) => [context.user]);
    const { companySettings, userData, userRole } = useAppSelector((state) => state.settings);
    const { possibleUserRoles, rolesFetchStatus } = usePossibleUserRoles();
    const authorizedPublisherSeats = getCompanySettings("authorized_publisher_seats");
    const seatsPerRole: Record<string, number> = JSON.parse(getCompanySettings("authorized_publisher_seats_per_role"));
    // At company creation all roles are setted up to 'Infinity'. So we need to check there it's isn't 'Infinity'
    const hasSeatsPerRole: boolean = !isObjectEmpty(seatsPerRole) && String(seatsPerRole?.owner) !== "Infinity";
    const userList = useNormalizedCompanyUsers();

    //Filter non admin users
    const filteredUsers = useMemo(() => {
        return userList.filter((user) => user.type !== "admin");
    }, [userList]);
    const isUsersLimitReached = filteredUsers.length.toString() === authorizedPublisherSeats;
    const [openAskForSeatsModal, setOpenAskForSeatsModal] = useState(false);

    /**
     * Number of seats without specific limit used.
     */
    const unlimitedRolesUsed = userList.filter((user) => !Object.keys(seatsPerRole).includes(user.role)).length ?? 0;

    /**
     * Total number of seats without limit.
     */
    const unlimitedRolesLength =
        authorizedPublisherSeats - Object.values(seatsPerRole).reduce((sum, value) => sum + value, 0);

    const [name, setName] = useState("");
    const [surname, setSurname] = useState("");
    const [email, setEmail] = useState("");
    const [role, setRole] = useState<ROLES | "">("");
    const [jobTitle, setJobTitle] = useState("");
    const accountType = getCognitoUserAttribute("custom:accountType", user);
    const [isDeleteMemberModalOpen, setIsDeleteMemberModalOpen] = useState(false);
    const isAdminOrOwner = [ROLES.OWNER].includes(userRole) || ["admin"].includes(accountType);

    const initialState = {
        userToDelete: { firstname: "", lastname: "", uuid: "" },
        userFormModal: { open: false, userId: null, defaultValues: {} } as TUserFormModal,
    };

    const [userToDelete, setUserToDelete] = useState<UserToDelete>(initialState.userToDelete);

    const [userFormModal, setUserFormModal] = useState<TUserFormModal>(initialState.userFormModal);

    // Keep the role state in sync with the first available option so the submitted
    // value always matches what the Select displays (the dropdown was previously
    // uncontrolled and could ship `OWNER` while showing another label).
    useEffect(() => {
        if (!role && possibleUserRoles.length > 0) {
            setRole(possibleUserRoles[0].role);
        }
    }, [possibleUserRoles, role]);

    const handleAddMemberModalClose = () => setIsModalOpen(false);

    const handleOpenDeleteModal = (userData: UserToDelete) => {
        setIsDeleteMemberModalOpen(true);
        setUserToDelete(userData);
    };

    const handleAddMember = (member: {
        name: string;
        surname: string;
        email: string;
        role: ROLES;
        jobTitle: string;
    }) => {
        // Find the selected role to check if it's custom
        const selectedRole = possibleUserRoles.find((r) => r.role === member.role);
        // Format role as "custom:{roleId}" for custom roles
        const rolePayload =
            selectedRole?.isCustom && selectedRole?.id ? formatCustomRoleId(selectedRole.id) : member.role;

        const newUser = {
            name: member.name,
            surname: member.surname,
            email: member.email?.trim(),
            accountType: "publisher" as TAccountType,
            companyId: companySettings!.id,
            role: rolePayload,
            jobTitle: member.jobTitle,
        };
        dispatch(sendInvitation(newUser));
        setIsModalOpen(false);
    };

    const handleDeleteMember = async (userData: UserToDelete) => {
        await dispatch(deleteUser(userData.uuid));
        setUserToDelete(initialState.userToDelete);
        setIsDeleteMemberModalOpen(false);
    };

    const formatNameColumn = (user: IUser) => {
        return (
            <div className={styles["member-list-name-col"]}>
                <div
                    className={styles["member-list-name-avatar"]}
                    style={{
                        backgroundColor: variables["purple-30-op"],
                    }}
                >
                    {(user.firstname?.slice(0, 1) ?? "").toLocaleUpperCase()}
                    {(user.lastname?.slice(0, 1) ?? "").toLocaleUpperCase()}
                </div>
                <div className={styles["member-list-firstname-col-right"]}>
                    <div className={styles["member-list-name-col-name"]}>
                        {user.firstname ?? ""} {user.lastname ?? ""}
                    </div>
                    <div title={user.email ?? ""}>
                        <CopyToClipboard icon={false} className={styles["member-list-name-col-email"]} fullWidth>
                            {user.email ?? ""}
                        </CopyToClipboard>
                    </div>
                </div>
            </div>
        );
    };

    const userMenuOptions = (userData: UserToDelete & { role: PossibleUserRole; jobTitle: string; userId: number }) => {
        const { firstname, lastname, role, jobTitle, userId } = userData;
        return [
            {
                id: 0,
                name: t("Members.EditUser"),
                action: () =>
                    setUserFormModal({ open: true, userId, defaultValues: { firstname, lastname, role, jobTitle } }),
            },
            {
                id: 1,
                name: t("Members.DeleteUser"),
                action: () => handleOpenDeleteModal(userData),
            },
        ] as ThreeDotMenuOptions[];
    };

    /**
     * Find the PossibleUserRole matching a user's role value.
     * Handles both predefined roles (e.g "owner") and custom roles (e.g. "custom:1").
     */
    const findUserRole = (roleValue: string | ROLES): PossibleUserRole | undefined => {
        if (isCustomRole(roleValue)) {
            const roleId = extractCustomRoleId(roleValue);
            return possibleUserRoles.find((r) => r.id === roleId);
        }
        return possibleUserRoles.find((r) => r.role === roleValue);
    };

    const usersColumns: LastElementTableColumns[] = [
        {
            id: "firstname",
            label: t("Members.NameUser"),
            align: "left",
            format: (value: string, id?: number, currentRow?: IUser) => currentRow && formatNameColumn(currentRow),
        },
        {
            id: "jobTitle",
            label: t("Settings.SignatoryTitle"),
            align: "left",
            format: (value) => value ?? "-",
        },
        {
            id: "role",
            label: t("Members.Role"),
            align: "left",
            format: (value: ROLES | string) => {
                // Handle custom role format "custom:{id}"
                if (isCustomRole(value)) {
                    const roleId = extractCustomRoleId(value);
                    const customRole = possibleUserRoles.find((r) => r.id === roleId);
                    return customRole?.label ?? value;
                }
                return getUserRolesLabel(value as ROLES) ?? value;
            },
        },
        {
            id: "created",
            label: t("Members.CreatedAt"),
            align: "left",
            format: (value: any) =>
                formatLocaleDateString({ date: value, options: { numeric: true, timeZone: "UTC" } }),
        },
        {
            id: "isVerified",
            label: t("Members.ActiveAccount"),
            align: "left",
            format: (value: number) => (value === 1 ? t("Members.Yes") : t("Members.No")),
        },
        {
            id: "",
            label: "",
            align: "center",
            hidden: !isAdminOrOwner,
            format: (value: any, id?: number, currentRow?: IUser) => {
                if (!currentRow) return;
                return (
                    <ThreeDotMenu
                        options={userMenuOptions({
                            firstname: currentRow?.firstname ?? "",
                            lastname: currentRow?.lastname ?? "",
                            uuid: currentRow.uuid,
                            role: findUserRole(currentRow?.role)!,
                            jobTitle: currentRow?.jobTitle ?? "",
                            userId: currentRow.userId,
                        })}
                    />
                );
            },
        },
    ];

    useEffect(() => {
        dispatch(getUserListByCompany());
        dispatch(fetchCompanyInfo());
    }, [dispatch]);

    const handleAddMemberAction = () => {
        if (isUsersLimitReached && accountType !== "admin") {
            setOpenAskForSeatsModal(true);
        } else setIsModalOpen(true);
    };

    return (
        <div className={styles["member-list"]}>
            {/** Ak for seats modal */}
            <AskForSeatsModal open={openAskForSeatsModal} onClose={() => setOpenAskForSeatsModal(false)} />
            {/* Add member modal */}
            <ModalBox
                open={isAddMemberModalOpen && isAdminOrOwner}
                onClose={handleAddMemberModalClose}
                mainTitle={`${t("Members.AddMember")} (${companySettings?.name ?? "-"})`}
            >
                <div className={styles["member-list-add-member-modal"]}>
                    <div className={styles["member-list-add-member-field"]}>
                        <div className={styles["member-list-add-member-label"]}>{t("Members.Name")}</div>
                        <StyledTextField name="firstname" placeholder="Jean" onBlur={(e) => setName(e.target.value)} />
                    </div>
                    <div className={styles["member-list-add-member-field"]}>
                        <div className={styles["member-list-add-member-label"]}>{t("Members.Surname")}</div>
                        <StyledTextField
                            name="lastname"
                            placeholder="Dupont"
                            onBlur={(e) => setSurname(e.target.value)}
                        />
                    </div>
                    <div className={styles["member-list-add-member-field"]}>
                        <div className={styles["member-list-add-member-label"]}>{t("Members.Role")}</div>
                        <StyledSelect
                            name="role"
                            value={role}
                            sx={{ fontFamily: "Poppins" }}
                            onChange={(e) => setRole(e.target.value as ROLES)}
                            renderValue={(selected: any) => <span>{getUserRolesLabel(selected)}</span>}
                            disabled={rolesFetchStatus !== "succeeded"}
                        >
                            {possibleUserRoles.map((item, i) => {
                                return (
                                    <StyledMenuItem key={`role-${i}`} value={item.role}>
                                        <div className={styles["role-select-menu"]}>
                                            <span className={styles["role"]}>{item.label}</span>
                                            <span className={styles["description"]}>{item.description}</span>
                                        </div>
                                    </StyledMenuItem>
                                );
                            })}
                        </StyledSelect>
                    </div>
                    <div className={styles["member-list-add-member-field"]}>
                        <div className={styles["member-list-add-member-label"]}>{t("Members.Email")}</div>
                        <StyledTextField
                            name="email"
                            placeholder="example@crealo.app"
                            onBlur={(e) => setEmail(e.target.value)}
                        />
                    </div>
                    <div className={styles["member-list-add-member-field"]}>
                        <div className={styles["member-list-add-member-label"]}>{t("Settings.SignatoryTitle")}</div>
                        <StyledTextField name="jobTitle" onBlur={(e) => setJobTitle(e.target.value)} />
                    </div>
                    <StyledButton
                        onClick={() => role && handleAddMember({ name, surname, email, role, jobTitle })}
                        buttonType="primary"
                    >
                        {t("Members.Validate")}
                    </StyledButton>
                </div>
            </ModalBox>

            {/* Delete member modal */}
            <DeleteModal
                open={isDeleteMemberModalOpen}
                onClose={() => setIsDeleteMemberModalOpen(false)}
                onDelete={() => handleDeleteMember(userToDelete)}
                elementTitle={t("Members.TheUser", {
                    name: userToDelete?.firstname ?? "",
                    surname: userToDelete?.lastname ?? "",
                })}
            />

            {/** Edit member modal */}
            <UserFormModal
                open={userFormModal.open}
                onClose={() => setUserFormModal(initialState.userFormModal)}
                defaultValues={userFormModal.defaultValues}
                possibleUserRoles={possibleUserRoles}
                userId={userFormModal.userId as number}
                isMe={userData?.id === userFormModal.userId}
            />

            {/* Header */}
            <div className={styles["member-list-header"]}>
                <div className={styles["member-list-title-wrapper"]}>
                    <h1 className={styles["member-list-title"]}>{t("Settings.Member")}</h1>
                    {/** Seats indication */}
                </div>
                <div className={styles["member-list-add-member"]}>
                    <Tooltip title={!isAdminOrOwner && t("Members.ActionRequiredAdmin")}>
                        <div>
                            <StyledButton
                                buttonType="primary"
                                onClick={handleAddMemberAction}
                                disabled={!isAdminOrOwner}
                            >
                                <AddCircleOutline sx={{ fontSize: "1rem", fill: "white" }} />
                                {t("Members.AddMember")}
                            </StyledButton>
                        </div>
                    </Tooltip>
                    {authorizedPublisherSeats !== "Infinity" && (
                        <div className={styles["member-list-seats-progress"]}>
                            <ProgressBar
                                label={t("Members.UsedSeats")}
                                value={filteredUsers.length}
                                total={authorizedPublisherSeats}
                                width={180}
                            />
                            {hasSeatsPerRole && (
                                <div className={styles["authorised-seats-container"]}>
                                    <SmallBody color="grey">{t("Members.Including")}</SmallBody>
                                    <div className={styles["authorised-seats-role"]}>
                                        {Object.keys(seatsPerRole).map((item) => {
                                            const usedSeats = filteredUsers.filter((user) => user.role === item);
                                            const seatsAllowed = seatsPerRole[item] ?? 0;
                                            return (
                                                <SmallBody color="grey" key={`seat-${item}`}>
                                                    {`${usedSeats.length}/${seatsAllowed} ${getUserRolesLabel(
                                                        item as ROLES,
                                                    ).toLowerCase()}`}
                                                </SmallBody>
                                            );
                                        })}
                                        {/* Removing this for now as it might be confusing. Also, typically if we define seats per role, the total should be the same */}
                                        {/* <SmallBody color="grey">{`${
                                            unlimitedRolesLength - unlimitedRolesUsed
                                        }/${unlimitedRolesLength} ${t("Members.OtherRoles")}`}</SmallBody> */}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* Member list */}
            <LastElementTable columns={usersColumns} data={userList} max={-1} dataType="users" />
        </div>
    );
};
export default MemberList;
